import { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion, useMotionValue, useSpring } from 'motion/react';
import { Dialog } from 'primereact/dialog';
import { BrainBreakCompleted } from './BrainBreakCompleted';
import { BrainBreakSurveySteps } from './BrainBreakSurveySteps';
import { CONFIDENCE_LEVELS } from './ConfidenceSlider';
import { EmotionPickerValue } from './EmotionPicker';
import { getSurveyQuestions, SurveyQuestion } from './surveyData';
import svgPaths from './svgPaths';
import { useBrainBreakRecorder } from '../../custom-hooks/useBrainBreakRecorder';
import { BRAIN_BREAK_TIMING, type BrainBreakPlacement } from '../../models/BrainBreak';
import { Clock3 } from 'lucide-react';

interface BrainBreakModalProps {
  isOpen: boolean;
  currentPlacement: BrainBreakPlacement | null;
  onClose: () => void;
  onComplete?: () => void;
  userId: string | undefined;
}

type HeaderPhase = 'entering' | 'settled';

const BrainBreakHeader: FC<{ phase: HeaderPhase }> = memo(({ phase }) => {
  const headerY = useMotionValue(0);
  const yellowSpring = useSpring(headerY, { stiffness: 80, damping: 12, mass: 0.8 });
  const tealSpring = useSpring(headerY, { stiffness: 60, damping: 10, mass: 1.2 });
  const pinkSpring = useSpring(headerY, { stiffness: 40, damping: 8, mass: 1.5 });

  return (
    <motion.header
      className="tw-text-center tw-relative"
      aria-label="Brain Break survey header"
      initial={{ opacity: 0, y: 300 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        y: { type: 'tween', duration: 0.9, ease: [0.25, 0.25, 0.2, 1] },
        opacity: { type: 'tween', duration: 0.3, ease: 'easeOut' },
      }}
      onUpdate={latest => {
        if (typeof latest.y === 'number') {
          headerY.set(latest.y);
        }
      }}
    >
      <div className="tw-flex tw-flex-col tw-gap-3 tw-items-center tw-relative tw-max-w-80 tw-m-auto">
        <p
          id="brain-break-title"
          className="tw-text-indigo-600 tw-font-atkinson tw-font-bold tw-text-xl tw-leading-6 tw-m-0 tw-relative tw-z-10"
        >
          Quick check in
        </p>

        <AnimatePresence>
          {phase === 'entering' && (
            <motion.div className="tw-relative tw-inline-block" exit={{ opacity: 0 }} transition={{ duration: 0.01 }}>
              <motion.div
                style={{
                  position: 'relative',
                  left: '-20px',
                  top: '50%',
                  y: yellowSpring,
                  pointerEvents: 'none',
                }}
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{
                  opacity: { duration: 0.01 },
                  scale: { duration: 0.01, ease: [0.16, 1, 0.3, 1] },
                }}
              >
                <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                  <ellipse cx="7.5" cy="7.5" rx="7.5" ry="7.5" fill="#FCB814" />
                </svg>
              </motion.div>

              <motion.div
                style={{
                  position: 'relative',
                  right: '-25px',
                  top: '-8px',
                  y: tealSpring,
                  rotate: -19,
                  pointerEvents: 'none',
                }}
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{
                  opacity: { duration: 0.01 },
                  scale: { duration: 0.01, ease: [0.16, 1, 0.3, 1] },
                }}
              >
                <svg width="28" height="43" viewBox="0 0 28 43" fill="none">
                  <rect
                    x="14.1633"
                    y="5.427e-05"
                    width="40"
                    height="15"
                    rx="7.5"
                    transform="rotate(70.7737 14.1633 5.427e-05)"
                    fill="#36C2DB"
                  />
                </svg>
              </motion.div>

              <motion.div
                style={{
                  position: 'relative',
                  left: '-30px',
                  bottom: '-5px',
                  y: pinkSpring,
                  rotate: 9.16,
                  pointerEvents: 'none',
                }}
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{
                  opacity: { duration: 0.02 },
                  scale: { duration: 0.02, ease: [0.16, 1, 0.3, 1] },
                }}
              >
                <svg width="22" height="42" viewBox="0 0 22 42" fill="none">
                  <rect
                    x="21.1764"
                    y="2.38794"
                    width="40"
                    height="15"
                    rx="7.5"
                    transform="rotate(99.1601 21.1764 2.38794)"
                    fill="#EB6FA5"
                  />
                </svg>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        <motion.div
          className="tw-flex tw-justify-center"
          initial={{ opacity: 0, y: 10 }}
          animate={phase === 'settled' ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
          transition={{
            type: 'spring',
            stiffness: 400,
            damping: 20,
            delay: phase === 'settled' ? 0.03 : 0,
          }}
        >
          <div
            data-size="sm"
            className="tw-h-4 tw-px-2.5 tw-py-0.5 tw-bg-neutral-200 tw-rounded-md tw-outline tw-outline-1 tw-outline-neutral-400 tw-inline-flex tw-justify-start tw-items-center tw-gap-2.5"
          >
            <Clock3 size={12} />
            <div className="tw-text-neutral-950 tw-text-xs tw-font-bold tw-font-atkinson tw-leading-4">1 min</div>
          </div>
        </motion.div>
      </div>
    </motion.header>
  );
});

BrainBreakHeader.displayName = 'BrainBreakHeader';

export const BrainBreakModal: FC<BrainBreakModalProps> = ({
  isOpen,
  currentPlacement,
  onClose,
  onComplete,
  userId,
}) => {
  const [step, setStep] = useState<1 | 2>(1);
  const [confidenceValue, setConfidenceValue] = useState(2);
  const [previewEmotion, setPreviewEmotion] = useState<EmotionPickerValue | null>(null);
  const [showCompletion, setShowCompletion] = useState(false);
  const [isSliderDragging, setIsSliderDragging] = useState(false);
  const [headerPhase, setHeaderPhase] = useState<HeaderPhase>('entering');
  const [submittingEmotion, setSubmittingEmotion] = useState<EmotionPickerValue | null>(null);
  const pendingTransitionRef = useRef<{ timeoutId: number } | null>(null);

  useEffect(() => {
    if (isOpen) {
      setHeaderPhase('entering');
      const timeoutId = window.setTimeout(() => {
        setHeaderPhase('settled');
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    document.body.classList.add('brain-break-open');
    return () => {
      document.body.classList.remove('brain-break-open');
    };
  }, [isOpen]);

  const surveyQuestions = useMemo(() => {
    if (!userId) return [];
    const today = new Date().toISOString().split('T')[0];
    return getSurveyQuestions(userId, today);
  }, [userId]);

  const currentQuestion: SurveyQuestion | undefined = surveyQuestions[0];
  const confidenceLabel = CONFIDENCE_LEVELS[confidenceValue]?.label;

  useEffect(() => {
    return () => {
      if (pendingTransitionRef.current) {
        clearTimeout(pendingTransitionRef.current.timeoutId);
        pendingTransitionRef.current = null;
      }
    };
  }, []);

  const { record } = useBrainBreakRecorder({
    onComplete: () => {
      const timeoutId = window.setTimeout(() => {
        setShowCompletion(true);
        pendingTransitionRef.current = null;
      }, BRAIN_BREAK_TIMING.STEP_TRANSITION_MS);
      pendingTransitionRef.current = { timeoutId };
    },
  });

  const handleConfidenceChange = useCallback((value: number) => {
    setConfidenceValue(value);
  }, []);

  const handleConfidenceSubmit = useCallback(() => {
    setStep(2);
  }, []);

  const handleEmotionPreview = useCallback((value: EmotionPickerValue | undefined) => {
    setPreviewEmotion(value ?? null);
  }, []);

  const handleEmotionSubmitStart = useCallback((value: EmotionPickerValue) => {
    setSubmittingEmotion(value);
  }, []);

  const handleEmotionSelection = useCallback(
    (value: EmotionPickerValue) => {
      setSubmittingEmotion(null);

      const started = record({
        questionId: currentQuestion?.id ?? '',
        questionType: currentQuestion?.type ?? '',
        confidenceValue,
        isReverseScored: currentQuestion?.isReverseScored,
        emotion: {
          id: value.id,
          valence: value.valence,
          arousal: value.arousal,
        },
        triggerPlacement: currentPlacement ?? null,
      });

      if (!started) {
        // Emotion selection ignored: recording already in progress
      }
    },
    [confidenceValue, currentQuestion, currentPlacement, record],
  );

  const confidenceProps = useMemo(
    () => ({
      value: confidenceValue,
      label: confidenceLabel,
      onChange: handleConfidenceChange,
      onSubmit: handleConfidenceSubmit,
      onDraggingChange: setIsSliderDragging,
    }),
    [confidenceValue, confidenceLabel, handleConfidenceChange, handleConfidenceSubmit],
  );

  const emotionProps = useMemo(
    () => ({
      value: previewEmotion,
      onSelect: handleEmotionSelection,
      onPreview: handleEmotionPreview,
      onSubmitStart: handleEmotionSubmitStart,
    }),
    [previewEmotion, handleEmotionSelection, handleEmotionPreview, handleEmotionSubmitStart],
  );

  const handleExitComplete = () => {
    onComplete?.();
    onClose();
  };

  if (!userId || !currentQuestion) {
    return null;
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          key="brain-break-wrapper"
          initial={{ y: '100%', opacity: 0.8 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          transition={{
            y: {
              duration: 0.88,
              ease: [0.25, 0.25, 0.2, 1],
            },
            opacity: {
              duration: 0.3,
              ease: 'easeIn',
            },
          }}
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
          }}
        >
          <Dialog
            visible={isOpen}
            position="bottom"
            style={{
              maxWidth: '100%',
              width: '100%',
              height: 'min(67vh, 520px)',
              borderRadius: '144px 144px 0 0',
              padding: '48px 80px 20px',
              margin: 0,
              cursor: isSliderDragging
                ? `url("data:image/svg+xml,${encodeURIComponent(svgPaths.handSvg)}") 15 8, pointer`
                : undefined,
            }}
            onHide={onClose}
            header={undefined}
            closable={false}
            draggable={false}
            resizable={false}
            dismissableMask={false}
            className="tw-relative !tw-max-w-full !tw-w-full !tw-bg-stone-100 tw-m-0 tw-gap-12"
            headerClassName="tw-border-0 !tw-bg-stone-100"
            contentClassName="tw-bg-stone-100 tw-overflow-hidden !tw-pt-0"
            transitionOptions={{ timeout: 0, classNames: '', addEndListener: () => {} }}
            pt={{
              header: { className: 'tw-hidden' },
            }}
          >
            <div
              className={`tw-flex tw-flex-col tw-justify-between tw-gap-12 tw-gap-y-8 ${
                showCompletion ? 'tw-h-full' : 'tw-h-max'
              }`}
            >
              {!showCompletion ? (
                <>
                  <BrainBreakHeader phase={headerPhase} />
                  <div
                    key={`survey-step-${step}`}
                    className={`tw-flex tw-flex-col ${step === 1 ? `tw-gap-12` : `tw-gap-4`}`}
                  >
                    <BrainBreakSurveySteps
                      step={step}
                      questionText={surveyQuestions[step - 1]?.questionText ?? ''}
                      confidence={confidenceProps}
                      emotion={emotionProps}
                    />
                  </div>
                  <motion.div
                    className="tw-flex tw-justify-center tw-gap-1.5"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <div
                      className="tw-h-1.5 tw-rounded-full tw-transition-all"
                      style={{
                        width: step === 1 ? '14px' : '13px',
                        backgroundColor: step === 1 ? '#4D4841' : '#BAAF9F',
                      }}
                    />
                    <div
                      className="tw-h-1.5 tw-rounded-full tw-transition-all"
                      style={{
                        width: step === 2 ? '14px' : '13px',
                        backgroundColor: step === 2 ? '#4D4841' : '#BAAF9F',
                      }}
                    />
                  </motion.div>
                </>
              ) : (
                <BrainBreakCompleted onExitComplete={handleExitComplete} />
              )}
            </div>
          </Dialog>
          <AnimatePresence>
            {!!submittingEmotion && (
              <motion.div
                key="emotion-message"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
                transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
                className="tw-fixed tw-z-[10000] tw-min-w-48 tw-px-6 tw-py-5 tw-bg-stone-50 tw-w-64 tw-rounded-3xl tw-inline-flex tw-flex-col tw-justify-start tw-items-center tw-gap-2 tw-outline tw-outline-4 -tw-outline-offset-4 tw-outline-neutral-100 tw-top-1/2 tw-left-[70%] md:max-lg:tw-top-[65%] md:max-lg:tw-left-[65%]"
                style={{
                  boxShadow: '0px 0px 24px -6px rgba(0,0,0,0.10), 0px 20px 25px -5px rgba(0,0,0,0.10)',
                  transform: 'translateY(-50%)',
                }}
              >
                <span className="tw-self-stretch tw-text-center tw-justify-start tw-text-neutral-950 tw-text-lg tw-font-normal tw-font-atkinson tw-leading-7 tw-italic">
                  {submittingEmotion.message}
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
